import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    DatePicker,
    TimePicker,
    Button,
    Modal,
    Avatar,
} from 'antd';
import {
    ChevronDown,
    ChevronRight,
    Search,
    Calendar,
    X,
    Plus,
    RefreshCw,
    Clock
} from 'lucide-react';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import Layout from '../../Layout'; // Giữ nguyên layout của bạn
import { Link } from "react-router-dom";

export default function AddAllocationForm() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // ================= STATES =================
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        assets: true,
        approvers: true,
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // State quản lý Modal thêm vị trí
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

    // Data Bảng Tài sản
    const [assets, setAssets] = useState([
        { id: 1, asset: 'macbook', qty: '1', location: undefined, deposit: 'Nhận đặt cọc', stock: '1/1', receiver: 'thi' },
    ]);

    const handleAddAsset = () => {
        setAssets([...assets, { id: Date.now(), asset: undefined, qty: '', location: undefined, deposit: '', stock: '0/1', receiver: undefined }]);
    };
    const handleRemoveAsset = (id) => {
        setAssets(assets.filter(a => a.id !== id));
    };

    // Data Người phê duyệt
    const [approvers, setApprovers] = useState([{ id: 1, user: undefined }]);
    const handleAddApprover = () => setApprovers([...approvers, { id: Date.now(), user: undefined }]);
    const handleRemoveApprover = (id) => setApprovers(approvers.filter(a => a.id !== id));

    // ================= MOCK DATA CHO CÁC Ô SELECT =================
    const assetOptions = [
        { value: '1', label: '1 - Máy tính', desc: 'Máy tính xách tay' },
        { value: 'macbook', label: '524 - MacBook Pro 13inch (M1, 2020)', desc: 'Máy tính xách tay' },
        { value: '8', label: '8 - Bộ phím chuột Logitech K235', desc: 'Điều hòa' },
        { value: '9', label: '9 - Bộ phím chuột Logitech K235', desc: 'Điều hòa' },
        { value: '10', label: '10 - Bộ phím chuột DareU LK186G', desc: 'Điều hòa' },
    ];

    const receiverOptions = [
        { value: 'thi', label: '00366 - Vũ Đình Thi', desc: 'TCTY Cổ Phần Bón và Hóa chất Dầu Khí - CTCP > Phòng kinh doanh' },
        { value: 'dung', label: '00362 - Đinh Tiến Dũng', desc: 'Tổng Công ty Phân bón và Hóa chất Dầu Khí - CTCP > Phòng kinh doanh 1 - HN' },
        { value: 'hiep', label: '00367 - Dương Hoàng Hiệp', desc: 'CÔNG TY CỔ PHẦN 1OFFICE > Phòng pháp chế' },
    ];

    const locationOptions = [
        { value: 'hcm', label: 'HCM' },
        { value: 'hcm1', label: 'HCM - Lầu 1' },
        { value: 'hcm2', label: 'HCM - Lầu 2' },
        { value: 'hcm3', label: 'HCM - Lầu 3' },
        { value: 'thaco', label: 'Thaco' },
    ];

    // ================= THEME =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
            controlHeight: 40,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const SectionHeader = ({ title, sectionKey }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    return (
        <Layout>
            {/* Header cố định */}
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 md:px-6 py-4 sticky top-[64px] z-10 shadow-sm">
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/assets/list" className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white">
                            <LeftOutlined className="text-[10px]" />
                        </div>
                    </Link>
                    <span className="text-000000e0 dark:text-gray-200 font-medium">Tạo mới cấp phát tài sản</span>
                </div>
            </div>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* Nội dung form */}
                    <div className="w-full mx-auto rounded-lg shadow-sm p-4 md:p-8 mt-6 dark:bg-gray-800 bg-white border dark:border-gray-700">

                        {/* --- 1. THÔNG TIN CHUNG --- */}
                        <SectionHeader title="Thông tin chung" sectionKey="general" />
                        {expandedSections.general && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6 max-w-2xl">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Mã CP <span className="text-red-500">*</span></label>
                                        <Input defaultValue="BBBG_116" suffix={<RefreshCw size={16} className="text-gray-400 cursor-pointer hover:text-[#3e91ab]" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Nhập tên cấp phát <span className="text-red-500">*</span></label>
                                        <Input defaultValue="Cấp phát máy tính" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Cấp phát cho</label>
                                    <Select defaultValue="CaNhan" className="w-full" options={[{ value: 'CaNhan', label: 'Cá nhân' }]} allowClear />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Người cấp phát</label>
                                    <Select defaultValue="huan" className="w-full" options={[{ value: 'huan', label: 'HQ_495 - Mr. Huấn' }]} allowClear />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Ngày cấp phát <span className="text-red-500">*</span></label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="24/08/2026" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Thời gian cấp phát <span className="text-red-500">*</span></label>
                                        <TimePicker className="w-full" format="HH:mm" placeholder="01:30" suffixIcon={<Clock size={16} className="text-gray-400" />} />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Địa điểm bàn giao</label>
                                    <Input defaultValue="Cty 67" />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Nhập dự kiến ngày trả</label>
                                    <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="29/08/2026" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Lý do cấp phát</label>
                                    <Input defaultValue="Cấp thiết bị demo sử dụng crm" className="focus:border-[#3e91ab] focus:ring-[#3e91ab]" />
                                </div>
                            </div>
                        )}

                        {/* --- 2. BẢNG TÀI SẢN --- */}
                        <div className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8">
                            <ChevronDown size={18} />
                            <span>Tài sản cấp phát</span>
                        </div>

                        {expandedSections.assets && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                {/* Thêm class no-scrollbar để ẩn thanh cuộn màu xám */}
                                <div className="overflow-x-auto no-scrollbar">
                                    <div className="min-w-[1000px]">
                                        {/* Header Row */}
                                        <div className="grid grid-cols-[3fr_1fr_1.5fr_1.5fr_1fr_2.5fr_30px] gap-2 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-1 items-end">
                                            <span>Tài sản <span className="text-red-500">*</span></span>
                                            <span>Số lượng <span className="text-red-500">*</span></span>
                                            <span>Vị trí tài sản</span>
                                            <span>Đặt cọc</span>
                                            <span>Tồn kho</span>
                                            <span>Người nhận</span>
                                            <span className="w-6"></span>
                                        </div>

                                        {/* Asset Rows */}
                                        {assets.map((item) => (
                                            <div key={item.id} className="grid grid-cols-[3fr_1fr_1.5fr_1.5fr_1fr_2.5fr_30px] gap-2 mb-3 items-center">

                                                {/* Cột 1: Chọn Tài sản */}
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Chọn"
                                                    value={item.asset}
                                                    className="w-full min-w-0"
                                                    // FIX: Nếu có dữ liệu thì ẩn icon Search để không bị đè lên dấu X
                                                    suffixIcon={!item.asset ? <Search size={14} className="text-gray-400" /> : <i className="hidden" />}
                                                    options={assetOptions}
                                                    optionRender={(option) => (
                                                        <div className="flex flex-col py-1 min-w-0">
                                                            <span className="font-medium text-000000e0 truncate">{option.data.label}</span>
                                                            <span className="text-[11px] text-gray-400 italic truncate">{option.data.desc}</span>
                                                        </div>
                                                    )}
                                                />

                                                {/* Cột 2: Số lượng */}
                                                <Input placeholder="Số lượng" value={item.qty} />

                                                {/* Cột 3: Vị trí tài sản */}
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Chọn Vị trí"
                                                    value={item.location}
                                                    className="w-full min-w-0"
                                                    options={locationOptions}
                                                    popupMatchSelectWidth={false}
                                                    dropdownStyle={{ width: 250 }}
                                                    dropdownRender={(menu) => (
                                                        <>
                                                            <div className="flex items-center gap-2 p-2 border-b border-gray-100 dark:border-gray-700">
                                                                <Input placeholder="Tìm kiếm..." size="small" className="flex-1" />
                                                                <Button
                                                                    size="small"
                                                                    icon={<Plus size={14} />}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        setIsLocationModalOpen(true);
                                                                    }}
                                                                />
                                                            </div>
                                                            {menu}
                                                        </>
                                                    )}
                                                />

                                                {/* Cột 4: Đặt cọc */}
                                                <Input placeholder="Nhận đặt cọc" value={item.deposit} />

                                                {/* Cột 5: Tồn kho */}
                                                <Input placeholder="Tồn kho" value={item.stock} className="bg-gray-50 dark:bg-gray-700 text-center" readOnly />

                                                {/* Cột 6: Người nhận */}
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Chọn người nhận / đại diện"
                                                    value={item.receiver}
                                                    className="w-full min-w-0"
                                                    // FIX: Nếu có dữ liệu thì ẩn icon Search để không bị đè lên dấu X
                                                    suffixIcon={!item.receiver ? <Search size={14} className="text-gray-400" /> : <i className="hidden" />}
                                                    options={receiverOptions}
                                                    optionRender={(option) => (
                                                        <div className="flex gap-3 py-1 items-start min-w-0">
                                                            <Avatar size={24} className="bg-gray-100 text-gray-500 shrink-0 mt-1">8</Avatar>
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="font-medium text-000000e0 truncate">{option.data.label}</span>
                                                                <span className="text-[11px] text-gray-400 leading-tight mt-0.5 truncate">{option.data.desc}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                />

                                                {/* Nút Xóa */}
                                                <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 shrink-0 ml-1" onClick={() => handleRemoveAsset(item.id)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button
                                    onClick={handleAddAsset}
                                    className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        )}

                        {/* --- 3. NGƯỜI PHÊ DUYỆT --- */}
                        <SectionHeader title="Người phê duyệt" sectionKey="approvers" />
                        {expandedSections.approvers && (
                            <div className="animate-in slide-in-from-top-2 mb-6 max-w-sm">
                                <label className="text-xs text-gray-500 mb-1 block">Nhân sự phê duyệt</label>
                                {approvers.map((item) => (
                                    <div key={item.id} className="flex items-center gap-2 mb-3">
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Tìm kiếm"
                                            className="w-full"
                                            value={item.user}
                                            onChange={(val) => {
                                                setApprovers(approvers.map(a => a.id === item.id ? { ...a, user: val } : a));
                                            }}
                                            // FIX: Nếu có dữ liệu thì ẩn icon Search để không bị đè lên dấu X
                                            suffixIcon={!item.user ? <Search size={14} className="text-gray-400" /> : <i className="hidden" />}
                                            options={receiverOptions}
                                        />
                                        {/* Chỉ hiện nút X xóa nếu có nhiều hơn 1 người duyệt */}
                                        {approvers.length > 1 && (
                                            <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 shrink-0" onClick={() => handleRemoveApprover(item.id)} />
                                        )}
                                    </div>
                                ))}
                                <button
                                    onClick={handleAddApprover}
                                    className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        )}

                        {/* --- FOOTER BUTTONS --- */}
                        <div className="flex gap-3 pt-6 mt-10 border-t dark:border-gray-700">
                            <Button type="primary" size="large" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab', fontWeight: 500 }}>
                                CẬP NHẬT
                            </Button>
                            <Button size="large" className="font-medium">
                                HỦY BỎ
                            </Button>
                        </div>
                    </div>

                    {/* ================= MODAL: TẠO MỚI VỊ TRÍ TÀI SẢN ================= */}
                    <Modal
                        title={<span className="text-000000e0 font-medium text-[15px]">Tạo mới vị trí tài sản</span>}
                        open={isLocationModalOpen}
                        onCancel={() => setIsLocationModalOpen(false)}
                        width={400}
                        closeIcon={<X size={18} className="text-[#3e91ab] hover:opacity-80" />}
                        footer={[
                            <Button key="back" onClick={() => setIsLocationModalOpen(false)}>
                                HỦY BỎ
                            </Button>,
                            <Button
                                key="submit"
                                type="primary"
                                style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }}
                                onClick={() => setIsLocationModalOpen(false)}
                            >
                                CẬP NHẬT
                            </Button>,
                        ]}
                    >
                        <div className="py-2 flex flex-col gap-4">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Vị trí <span className="text-red-500">*</span></label>
                                <Input className="focus:border-[#3e91ab] focus:ring-[#3e91ab]" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Thuộc nhóm cha</label>
                                <Select defaultValue="thaco" className="w-full" options={[{ value: 'thaco', label: 'Thaco' }]} allowClear />
                            </div>
                        </div>
                    </Modal>

                </div>
            </ConfigProvider>
        </Layout>
    );
}